import {
  createRouter as _createRouter,
  createWebHistory,
  isNavigationFailure,
  NavigationFailureType,
  START_LOCATION,
} from 'vue-router'
import type {
  Router,
  RouteLocationNormalized,
  NavigationGuardNext,
} from 'vue-router'
import routes from './routes'

import { updateMetaTags } from '@/helpers/router'
import { auth } from '@/services/auth'
import { maybeSyncAnalyticsIds } from '@/analytics/logic'
import { AdjustWrapper } from '@/analytics/adjust'
import { Mixpanel } from '@/analytics/mixpanel'
import { appPositionTrackingService } from '@/services/app.position.tracking'
import { notificationService } from '@/services/notification'
import { backLink } from '@/models/backlink'
import { initWeglotIfNeeded } from '@/services/weglot'
import { createChangelogView } from '@/models/changelog'
import { detectAndSaveMarketingUrl } from '@/analytics/mixpanel/base'
import { platform } from '@/services/platform'
import { Sentry } from '@/services/sentry'
import { backend } from '@/services/backend'
import { appNotifications } from '@/services/notification.app'
import { storage } from '@/helpers/storage'
import { StorageKeys } from '@/helpers/storage.keys'
import { extractDocSlugFromRoute } from '@/helpers/billingSourceBook'
import {
  getUpgradePromptState,
  setUpgradePromptState,
} from '@/models/billing.upgrade.prompt'
import { hardGateBilling20516Redirect } from '@/models/hardGateBilling20516'
import { saveForLater20321Redirect } from '@/models/docs.list'
import { playerSession } from '@/services/player'

export function createRouter(): Router {
  const router = _createRouter({
    history: createWebHistory(),
    routes: routes,
    scrollBehavior(
      to: RouteLocationNormalized,
      from: RouteLocationNormalized,
      savedPosition: any,
    ): any {
      // If there is an anchor tag in the url and the tag is an element,
      // we scroll to that element, smoothly.
      if (to.hash && document.querySelector(to.hash)) {
        return { el: to.hash, behavior: 'smooth' }
      }

      if (savedPosition) {
        return savedPosition
      }

      const app = document.querySelector('#app')
      // We already have a scroll position control for `book/` pages/routes.
      // We scroll to the top of the screen on every other page visit except the `book/` page.
      if (app && to.path !== '/' && !to.path.includes('book/')) {
        app.scrollTo(0, 0)
      }

      return { top: 0, left: 0 }
    },
  })

  // Variant B of exp_save_for_later_20321: old My Library tabs and list URLs
  // open the new Library page or its list pages.
  // Put this first because a redirect reruns every guard, which would count
  // the page twice (one for the old route, one for the new route) towards the
  // number of visits until we show a free user an upgrade prompt.
  router.beforeEach((to, from, next) => {
    const redirect = saveForLater20321Redirect(to)
    if (redirect) {
      return next(redirect)
    }
    next()
  })

  // Capture the book the user navigated from when going to billing, so
  // checkout can personalize using ?source_book=<url_slug>.

  router.beforeEach((to, from, next) => {
    if (to.name !== 'billing') {
      return next()
    }

    // Keep explicit param if already set
    if (to.query.source_book) {
      return next()
    }

    const slug = extractDocSlugFromRoute(from)
    if (!slug) {
      return next()
    }

    return next({
      name: to.name,
      params: to.params,
      query: { ...to.query, source_book: slug },
      hash: to.hash,
    })
  })

  // Mark the upgrade prompt as pending when a free user navigates away
  // from the billing page.
  router.beforeEach((to, from, next) => {
    if (
      from.name === 'billing' &&
      auth.isFreePlan() &&
      getUpgradePromptState() !== 'shown'
    ) {
      setUpgradePromptState('pending')
    }

    next()
  })

  // We track the number of pages a free user has visited while an upgrade
  // prompt is pending, so we can show it after a few navigations.
  // We also don't track visits from and to the billing.
  router.beforeEach((to, from, next) => {
    if (
      getUpgradePromptState() === 'pending' &&
      from.name !== 'billing' &&
      to.name !== 'billing'
    ) {
      const pageVisits = Number(
        localStorage.getItem(StorageKeys.upgradePromptPagesVisited),
      )
      storage.setItem(
        StorageKeys.upgradePromptPagesVisited,
        String(pageVisits + 1),
      )
    }

    next()
  })

  // Check if the service worker found updates and set the `sf_needs_update` flag.
  // See frontend/src/init/registerServiceWorker.ts
  router.beforeEach((to, from, next) => {
    // Skip the first navigation after a page load and keep the flag for the
    // next one: the page has just loaded, so there's no need to reload it.
    // In the iOS app, the WebView reloads the page when the app comes back
    // after a long time in the background, and a second reload would lose
    // the link of a push notification tapped at that moment.
    if (from === START_LOCATION) {
      return next()
    }

    const needsUpdate = localStorage.getItem(StorageKeys.needsUpdate)
    if (needsUpdate && needsUpdate === 'true') {
      localStorage.removeItem(StorageKeys.needsUpdate)

      // A link to a page added in the new version shows as not found in the
      // current one, so we reload to the original link instead.
      const unknownLink =
        to.name === 'not_found' || to.name === 'public_not_found'
          ? to.redirectedFrom
          : undefined

      // We add the origin to keep all links inside Shortform.
      window.location.href =
        window.location.origin + (unknownLink ?? to).fullPath
    }

    next()
  })

  // Dismiss toast notifications.
  router.beforeEach((to, from, next) => {
    appNotifications.notifications.forEach((notification) => {
      if (notification.dismissible) {
        appNotifications.dismiss(notification.id)
      }
    })

    next()
  })

  // Variant B of exp_hard_gate_billing_20516: a new free account can only open
  // billing, settings, support, and public pages until a trial starts.
  router.beforeEach((to, from, next) => {
    const redirect = hardGateBilling20516Redirect(to)
    if (redirect) {
      return next(redirect)
    }
    next()
  })

  // Handle navigation to the external billing page
  // for Stripe payments on iOS.
  router.beforeEach(
    async (
      to: RouteLocationNormalized,
      from: RouteLocationNormalized,
      next: NavigationGuardNext,
    ) => {
      if (to.name !== 'billing_apple_external') {
        return next()
      }

      // This page should only be accessed
      // via the in-app browser of the iOS app.
      const deviceInfo = await platform.deviceInfo()
      if (deviceInfo.platform !== 'web') {
        Sentry.captureMessage(
          'Navigation to apple external billing page' +
            `on unauthorized platform: ${deviceInfo.platform}`,
        )
        return next({ name: 'billing' })
      }

      const authToken = to.query.authToken16218
      if (authToken) {
        localStorage.setItem(StorageKeys.authToken, authToken as string)

        // Set the user data from the auth token.
        await backend.updateUserData()

        // Remove the auth_token from the URL.
        next({
          name: to.name,
          query: {
            ...to.query,
            authToken16218: undefined,
          },
        })
      } else if (auth.loggedIn()) {
        // The `next` call above will cause this
        // route guard to be triggered again, but
        // this time without the token in the url.
        next()
      } else {
        Sentry.captureMessage(
          'Unexpected navigation to apple external billing page',
        )
        // The user is not logged in,
        // so redirect them to the signup page.
        next({ name: 'signup' })
      }
    },
  )

  router.beforeEach(
    async (
      to: RouteLocationNormalized,
      from: RouteLocationNormalized,
      next: NavigationGuardNext,
    ) => {
      const pageName = (to.name as string) || ''
      const isPublicPage = to.meta.isPublicPage as boolean
      const isSignupPage = pageName === 'signup'
      const isLoginPage = pageName === 'login'

      initWeglotIfNeeded()

      // Refresh the changelog data for native devices as we don't have page loads
      if (!isPublicPage && auth.loggedIn()) {
        createChangelogView().then((changelogView) => {
          changelogView.refreshIfNeeded()
        })
      }
      const isUTMEmail = to.query.utm_source && to.query.utm_source === 'email'
      if (!auth.loggedIn() && !isPublicPage) {
        if (auth.previouslyLoggedIn() || isUTMEmail) {
          next({
            name: 'login',
            query: { redirect: to.path, ...to.query },
          })
        } else {
          next({
            name: 'signup',
            // Pass path and query separately so we get e.g. ?redirect=/app&utm_medium=partner
            // instead of ?redirect=/app?utm_medium=partner (invalid: second ? breaks parsing).
            query: { redirect: to.path, ...to.query },
          })
        }
      }
      // If we are logged in and try to visit signup/login page
      else if (auth.loggedIn() && (isSignupPage || isLoginPage)) {
        if (auth.isTemporaryUser()) {
          // Let temporary users from the app_browse_15807 experiment visit the auth pages.
          return next()
        }

        detectAndSaveMarketingUrl()
        // We have the `redirect` parameter in the URL query.
        const redirectQuery = to.query?.redirect
        if (redirectQuery) {
          next({ path: String(redirectQuery) })
        }
        // Redirect to home
        else {
          next({ name: 'discover' })
        }
      } else {
        next() // Make sure to always call next()!
      }
    },
  )

  router.beforeEach(
    async (
      to: RouteLocationNormalized,
      from: RouteLocationNormalized,
      next: NavigationGuardNext,
    ) => {
      updateMetaTags(to, true)
      next()
    },
  )

  /*
   * This functionality is disabled because it adds a possibility
   * of user to stuck inside questionnaire without possibility to
   * to to the application itself
  router.beforeEach(async (to, from, next) => {
    // Redirect to questionnaire if it wasn't finished
    const step = storage.getItem(StorageKeys.onboardingQuestionStep) as string
    // If the step is not 'finish' and we are not at questionnaire - redirect
    if (
      step !== 'questionnaire_finish' &&
      !(to.name as string).includes('questionnaire')
    ) {
      next({ name: step })
    } else {
      next()
    }
  })
  */

  // Check and, if needed, sync the analytics IDs to the backend. We do this
  // here, not in App.vue, so that it will run even if the user does not start
  // a new browser tab/session. In particular, after signup, this will sync the
  // analytics ID on the next route accessed.
  router.beforeEach(
    async (
      to: RouteLocationNormalized,
      from: RouteLocationNormalized,
      next: NavigationGuardNext,
    ) => {
      // We don't sync the analytic IDs in the external billing page
      // because we wait to track user data when they come back to the
      // native app.
      if (to.name !== 'billing_apple_external') {
        maybeSyncAnalyticsIds()
      }
      next()
    },
  )

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  router.afterEach(async (to: RouteLocationNormalized) => {
    if ((window as any)['__PRERENDER_INJECTED']) {
      // Skip Adjust and Mixpanel page tracking during prerendering.
      return
    }

    if (to.name === 'search' && to.query.term && to.query.term !== '') {
      // We track search queries from the Search page beforeRouteLeave() guard.
      return
    }

    if (to.name === 'onboarding') {
      await AdjustWrapper.trackPageViewSignup()
    }

    // Delay a page view call a bit to wait for window.locaion.href to change
    // to the new URL
    // See
    // - https://github.com/vuejs/vue-router/issues/1197
    // - https://github.com/vuejs/vue-router/pull/2292
    // - https://github.com/vuejs/vue-router/issues/2079
    setTimeout(() => {
      Mixpanel.pageView(to.params)
    }, 100)
  })

  // Check for a startup position; if there is one, navigate to it.
  // But if _gotUrlEvent is set, we skip this; we don't want to override
  // or navigate away from the launched URL.
  router.beforeEach(async (to, from, next) => {
    // This only returns a value at startup time.
    const restoredPosition = appPositionTrackingService.nextPositionOnRedirect(
      to,
      from,
    )
    if (restoredPosition && !_gotUrlEvent) {
      if (router.resolve(restoredPosition).meta.isDocContentPage) {
        playerSession.value.setNextArrival('relaunch')
      }
      next(restoredPosition)
      return
    }
    next()
  })

  // Save how the user reached a reading page for a later `Audio Play` event.
  // Clear unused values on other pages so they cannot affect a later guide.
  router.afterEach((to, from, failure) => {
    // Another click or deep link can update the pending arrival before Vue
    // reports an earlier navigation as cancelled.
    if (isNavigationFailure(failure, NavigationFailureType.cancelled)) {
      return
    }
    if (
      failure &&
      !isNavigationFailure(failure, NavigationFailureType.duplicated)
    ) {
      playerSession.value.clearNextArrival()
      return
    }

    if (!to.meta.isDocContentPage) {
      playerSession.value.clearNextArrival()
      return
    }

    if (
      Boolean(from.meta.isDocContentPage) &&
      to.name === from.name &&
      to.params.url_slug === from.params.url_slug
    ) {
      // Navigation between chapters of the same guide does not change how
      // the user originally reached the guide.
      playerSession.value.applyPendingGuideArrival()
    } else {
      // Navigating to a different guide
      playerSession.value.applyPendingGuideArrival('in_guide')
    }
  })

  router.beforeEach(async (to, from, next) => {
    backLink.value.updateHistory(to, from)
    next()
  })

  // Track URL changes so we can restore app position when the user switches to
  // a different app and this app reloads from scratch.
  router.afterEach((route: RouteLocationNormalized) =>
    appPositionTrackingService.storePositionOnChange(route),
  )

  // Initialize notifications.
  // TODO The initialization request can be slow (1s) at times.
  router.afterEach(async (route: RouteLocationNormalized) => {
    // Initialize firebase when the user is authenticated and not on a public page.
    // An extra check is done to see if initialization can happen on the current page
    if (auth.loggedIn() && !route.meta.isPublicPage) {
      await notificationService.init()
    }
  })

  return router
}

let _gotUrlEvent = false

/**
 * Called if we get a native appUrlOpen event. This only matters if
 * we have not yet done the initial move to the startup position.
 */
export function gotUrlEvent(): void {
  _gotUrlEvent = true
}

export const router = createRouter()
